import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Clock, 
  Users, 
  DollarSign,
  TrendingUp,
  CheckCircle2,
  Warehouse,
  Factory,
  Truck,
  Briefcase,
  HeadphonesIcon,
  ClipboardCheck,
  Search,
  UserCheck
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

export default function EmployersPage() {
  const location = useLocation();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    workersNeeded: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (location.hash === '#request-form') {
      setTimeout(() => {
        const element = document.getElementById('request-form');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Mock submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Request submitted successfully!', {
      description: 'Our team will contact you within 24 hours.'
    });
    
    setFormData({
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      workersNeeded: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const services = [
    {
      title: 'Temporary Staffing',
      description: 'Quick deployment of skilled workers for short-term projects, seasonal demands, or unexpected absences.',
      icon: Clock,
      features: ['24-48 hour deployment', 'Flexible contracts', 'No long-term commitment']
    },
    {
      title: 'Temp-to-Perm',
      description: 'Try before you hire. Evaluate workers on the job before making a permanent hiring decision.',
      icon: Users,
      features: ['Risk-free evaluation', 'Seamless transition', 'Reduced hiring costs']
    },
    {
      title: 'Direct Hiring',
      description: 'Let us handle the entire recruitment process to find permanent employees for your team.',
      icon: UserCheck,
      features: ['End-to-end recruitment', 'Pre-screened candidates', 'Guaranteed placements']
    },
    {
      title: 'Workforce Management',
      description: 'Comprehensive solutions for managing your contingent workforce effectively.',
      icon: ClipboardCheck,
      features: ['Payroll handling', 'Compliance management', 'Performance tracking']
    }
  ];

  const benefits = [
    {
      title: 'Reduce Hiring Time',
      description: 'Cut your time-to-hire by up to 70% with our pre-screened candidate pool.',
      icon: Clock,
      stat: '70%'
    },
    {
      title: 'Lower Costs',
      description: 'Save on recruitment, training, and administrative overhead costs.',
      icon: DollarSign,
      stat: '40%'
    },
    {
      title: 'Scalable Workforce',
      description: 'Easily scale up or down based on your business demands.',
      icon: TrendingUp,
      stat: 'Flexible'
    }
  ];

  const industries = [
    { name: 'Warehousing & Logistics', icon: Warehouse },
    { name: 'Manufacturing', icon: Factory },
    { name: 'Transportation', icon: Truck },
    { name: 'Administrative & Office', icon: Briefcase },
    { name: 'Customer Service', icon: HeadphonesIcon }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Requirement Intake',
      description: 'Share your staffing needs, job requirements, and timeline. We listen and understand your unique challenges.'
    },
    {
      step: '02',
      title: 'Screening & Selection',
      description: 'We source candidates from our talent pool, conduct thorough screenings, and verify qualifications.'
    },
    {
      step: '03',
      title: 'Placement & Onboarding',
      description: 'Selected candidates are placed at your site with proper onboarding and ongoing support.'
    }
  ];

  return (
    <main data-testid="employers-page">
      {/* Hero Section */}
      <section className="page-header" data-testid="employers-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm font-medium mb-6">
              For Employers
            </span>
            <h1 className="font-['Chivo'] font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight tracking-tight mb-6">
              Flexible Staffing Solutions for Your Business
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              From temporary workers to permanent hires, we provide the workforce you need to keep your operations running smoothly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white" data-testid="employer-services">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              Our Services
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              Tailored Staffing Solutions
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border border-[#E2E8F0] p-8 bg-white hover:border-[#1E90FF] transition-colors"
              >
                <div className="w-14 h-14 bg-[#F5F7FA] rounded-lg flex items-center justify-center mb-6">
                  <service.icon size={28} className="text-[#1E90FF]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Chivo'] font-bold text-xl text-[#0B1F3A] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#64748B] mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-[#0B1F3A]">
                      <CheckCircle2 size={16} className="text-[#FF6B00]" strokeWidth={2} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="employer-benefits">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              Benefits
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              Why Partner With Us
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-[#E2E8F0] p-8 text-center"
              >
                <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon size={28} className="text-[#1E90FF]" strokeWidth={1.5} />
                </div>
                <div className="font-['Chivo'] font-black text-4xl text-[#FF6B00] mb-2">
                  {benefit.stat}
                </div>
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-2">
                  {benefit.title}
                </h3>
                <p className="text-[#64748B] text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section-padding bg-white" data-testid="employer-industries">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              Industries Served
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              Deep Expertise Across Sectors
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border border-[#E2E8F0] p-6 text-center hover:border-[#1E90FF] hover:bg-[#F5F7FA] transition-colors group"
              >
                <div className="w-12 h-12 bg-[#F5F7FA] group-hover:bg-white rounded-lg flex items-center justify-center mx-auto mb-3 transition-colors">
                  <industry.icon size={24} className="text-[#1E90FF]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Chivo'] font-bold text-sm text-[#0B1F3A]">
                  {industry.name}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="employer-process">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm text-[#94A3B8] uppercase tracking-[0.2em] font-bold">
              Our Process
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-white mt-3 tracking-tight">
              How We Work With You
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-[120px] font-['Chivo'] font-black text-white/5 absolute -top-8 left-0 leading-none">
                  {step.step}
                </div>
                <div className="relative z-10 pt-16">
                  <div className="w-12 h-12 rounded-full bg-[#FF6B00] text-white font-['Chivo'] font-bold flex items-center justify-center mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-['Chivo'] font-bold text-xl text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#94A3B8] leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Request Form Section */}
      <section id="request-form" className="section-padding bg-white" data-testid="request-form-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
                Get Started
              </span>
              <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight mb-6">
                Request Staff Today
              </h2>
              <p className="text-[#64748B] text-lg leading-relaxed mb-8">
                Fill out the form and our team will contact you within 24 hours to discuss your staffing needs.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F5F7FA] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-[#FF6B00]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-['Chivo'] font-bold text-[#0B1F3A]">Fast Response</h4>
                    <p className="text-sm text-[#64748B]">Get a response within 24 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F5F7FA] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Search size={20} className="text-[#FF6B00]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-['Chivo'] font-bold text-[#0B1F3A]">No Obligation</h4>
                    <p className="text-sm text-[#64748B]">Free consultation with no commitment</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F5F7FA] rounded-lg flex items-center justify-center flex-shrink-0">
                    <UserCheck size={20} className="text-[#FF6B00]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-['Chivo'] font-bold text-[#0B1F3A]">Expert Matching</h4>
                    <p className="text-sm text-[#64748B]">Tailored solutions for your needs</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="bg-[#F5F7FA] p-8 border border-[#E2E8F0]" data-testid="employer-request-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                      Company Name *
                    </label>
                    <Input
                      id="companyName"
                      name="companyName"
                      type="text"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      placeholder="Your company"
                      className="w-full"
                      data-testid="input-company-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="contactName" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                      Contact Name *
                    </label>
                    <Input
                      id="contactName"
                      name="contactName"
                      type="text"
                      value={formData.contactName}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="w-full"
                      data-testid="input-contact-name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                      Email *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="email@company.com"
                      className="w-full"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                      Phone *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="123-456-7890"
                      className="w-full"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="workersNeeded" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                    Number of Workers Needed
                  </label>
                  <Input
                    id="workersNeeded"
                    name="workersNeeded"
                    type="text"
                    value={formData.workersNeeded}
                    onChange={handleChange}
                    placeholder="e.g., 5-10 workers"
                    className="w-full"
                    data-testid="input-workers-needed"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                    Tell us about your needs
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe your staffing requirements, timeline, and any specific skills needed..."
                    rows={4}
                    className="w-full"
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full justify-center text-lg py-4 disabled:opacity-50"
                  data-testid="submit-request-form"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  {!isSubmitting && <ArrowRight size={20} className="arrow-icon" strokeWidth={2} />}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
